import React, { useState } from 'react';
import { Tabs, Table, Button, Space, Tag, Input, Empty, Spin, Modal, Form, message } from 'antd';
import { UserOutlined, CalendarOutlined, SearchOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import type { ApprovedApplication, SentFavouriteRequest } from './ReferanslarTab.types';

const ReferanslarTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('candidates');
  const [searchCandidates, setSearchCandidates] = useState('');
  const [searchSent, setSearchSent] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ApprovedApplication | null>(null);
  const [form] = Form.useForm();

  const { data: candidates = [], isLoading: loadingCandidates, refetch: refetchCandidates } = useQuery({
    queryKey: mentorProfileQueryKeys.favouriteCandidates,
    queryFn: async () => {
      const res = await apiService.getFavouriteCandidates();
      if (!res.success) throw new Error(res.message);
      return res.data ?? [];
    },
  });

  const { data: sentList = [], isLoading: loadingSent, refetch: refetchSent } = useQuery({
    queryKey: mentorProfileQueryKeys.sentFavouriteRequests,
    queryFn: async () => {
      const res = await apiService.getSentFavouriteRequests();
      if (!res.success) throw new Error(res.message);
      return res.data ?? [];
    },
  });

  const createMutation = useMutation({
    mutationFn: (body: { type: number; applicationId: number; targetUserId: number; message?: string }) =>
      apiService.createFavouriteRequest(body),
    onSuccess: (res) => {
      if (res.success) {
        message.success('Referans talebi gönderildi.');
        setModalVisible(false);
        setSelectedRow(null);
        form.resetFields();
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.favouriteCandidates });
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.sentFavouriteRequests });
      } else message.error(res.message || 'Gönderilemedi.');
    },
    onError: () => message.error('Bir hata oluştu.'),
  });

  const getTypeText = (type: number) => (type === 0 ? '1-1 Görüşme' : 'Bootcamp');
  const getTypeColor = (type: number) => (type === 0 ? 'blue' : 'green');
  const getTypeIcon = (type: number) => (type === 0 ? <UserOutlined /> : <CalendarOutlined />);

  const openSendModal = (row: ApprovedApplication) => {
    setSelectedRow(row);
    form.setFieldsValue({ message: '' });
    setModalVisible(true);
  };

  const handleSend = () => {
    if (!selectedRow) return;
    form.validateFields().then((values) => {
      createMutation.mutate({
        type: selectedRow.type,
        applicationId: selectedRow.id,
        targetUserId: selectedRow.menteeId,
        message: values.message || undefined,
      });
    });
  };

  const filteredCandidates = candidates.filter(
    (c: ApprovedApplication) =>
      (c.name || '').toLowerCase().includes(searchCandidates.toLowerCase()) ||
      getTypeText(c.type).toLowerCase().includes(searchCandidates.toLowerCase())
  );
  const filteredSent = sentList.filter(
    (s: SentFavouriteRequest) =>
      (s.menteeName || '').toLowerCase().includes(searchSent.toLowerCase()) ||
      (s.status || '').toLowerCase().includes(searchSent.toLowerCase())
  );

  const candidateColumns = [
    {
      title: 'Mentee / Bağ',
      key: 'name',
      render: (_: unknown, record: ApprovedApplication) => (
        <Space>
          <span style={{ fontWeight: 500 }}>{record.name}</span>
          <Tag color={getTypeColor(record.type)} icon={getTypeIcon(record.type)}>{getTypeText(record.type)}</Tag>
        </Space>
      ),
    },
    { title: 'Mentee ID', dataIndex: 'menteeId', key: 'menteeId', render: (id: number) => `#${id}` },
    {
      title: 'İşlem',
      key: 'action',
      render: (_: unknown, record: ApprovedApplication) => (
        <Button type="primary" size="small" icon={<SendOutlined />} onClick={() => openSendModal(record)}>
          Referans isteği gönder
        </Button>
      ),
    },
  ];

  const sentColumns = [
    {
      title: 'Mentee',
      dataIndex: 'menteeName',
      key: 'menteeName',
      render: (name: string, record: SentFavouriteRequest) => (
        <Space>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => window.open(`/mentee/${record.menteeId}`, '_blank')}
          >
            {name}
          </Button>
          <Tag color={getTypeColor(record.type)}>{getTypeText(record.type)}</Tag>
        </Space>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'Accepted' ? 'green' : 'orange'}>{status === 'Accepted' ? 'Kabul' : 'Beklemede'}</Tag>
      ),
    },
    { title: 'Tarih', dataIndex: 'createdAt', key: 'createdAt', render: (d: string) => new Date(d).toLocaleDateString('tr-TR') },
  ];

  return (
    <>
    <PanelTabCard
      title="Referanslar"
      description="Referans (favori) talebi gönderebileceğiniz menteeleri görün ve gönderdiğiniz talepleri takip edin."
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Referans isteği göndermediği menteeler" key="candidates">
          <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Input
              placeholder="Mentee veya tip ara..."
              prefix={<SearchOutlined />}
              value={searchCandidates}
              onChange={(e) => setSearchCandidates(e.target.value)}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refetchCandidates()} loading={loadingCandidates}>Yenile</Button>
          </div>
          {loadingCandidates ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
          ) : filteredCandidates.length === 0 ? (
            <Empty
              description={searchCandidates ? 'Arama sonucu yok' : 'Referans isteği gönderebileceğiniz mentee bulunmuyor'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Ders verdiğiniz (1-1 veya bootcamp) ve henüz referans talebi göndermediğiniz mentee burada listelenir.
              </span>
            </Empty>
          ) : (
            <Table rowKey={(r) => `${r.type}-${r.id}-${r.menteeId}`} columns={candidateColumns} dataSource={filteredCandidates} pagination={{ pageSize: 10 }} />
          )}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Gönderdiğim referans talepleri" key="sent">
          <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Input
              placeholder="Mentee veya durum ara..."
              prefix={<SearchOutlined />}
              value={searchSent}
              onChange={(e) => setSearchSent(e.target.value)}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refetchSent()} loading={loadingSent}>Yenile</Button>
          </div>
          {loadingSent ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
          ) : filteredSent.length === 0 ? (
            <Empty
              description="Henüz referans talebi göndermediniz"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Gönderdiğiniz referans talepleri burada görünür. Mentee kabul ederse profilinde siz referans veren mentor olarak listelenirsiniz.
              </span>
            </Empty>
          ) : (
            <Table rowKey="id" columns={sentColumns} dataSource={filteredSent} pagination={{ pageSize: 10 }} />
          )}
        </Tabs.TabPane>
      </Tabs>
    </PanelTabCard>

      <Modal
        title="Referans talebi gönder"
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setSelectedRow(null); }}
        onOk={handleSend}
        confirmLoading={createMutation.isPending}
        okText="Gönder"
      >
        {selectedRow && (
          <>
            <p style={{ marginBottom: 8 }}><strong>{selectedRow.name}</strong> için referans (favori) talebi göndereceksiniz.</p>
            <p style={{ marginBottom: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
              Reddedilen taleplerde 1 gün boyunca tekrar istek gönderemezsiniz.
            </p>
          </>
        )}
        <Form form={form} layout="vertical">
          <Form.Item name="message" label="Mesaj (isteğe bağlı)">
            <Input.TextArea rows={3} placeholder="Mentee'ye iletmek istediğiniz kısa mesaj" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ReferanslarTab;
